/**
 * How a picture is shown on an event. Settings live on the event as `display[url] = { fit, aspect, focus }`
 * (see backend/image_display.py); pictures without an entry follow the automatic rule below.
 *
 * - fit     «auto» | «contain» (whole picture – posters, anything with text) | «cover» (fill the frame – photographs)
 * - aspect  «auto» | one of ASPECTS – the frame's width:height. Auto = the picture's own ratio when it is shown whole,
 *           the house's standard 4:3 frame when it fills.
 * - focus   { x, y } in percent – the point that must stay in view when the picture fills the frame.
 *
 * Automatic rule: a picture is shown whole when the standard frame would crop away more than ~15 % of it
 * (portrait posters, squares, wide panoramas); a photograph close to 4:3 fills the frame as before.
 */
export const FITS = [
  { value: "auto", label: "Automatisk", hint: "Heile biletet når ramma ville kutte mykje av det, elles fyll ramma" },
  { value: "contain", label: "Heile biletet", hint: "Plakatar og bilete med tekst – ingenting vert kutta" },
  { value: "cover", label: "Fyll ramma", hint: "Foto – vel kva punkt som alltid skal vere med" },
];

export const ASPECTS = [
  { value: "auto", label: "Automatisk", ratio: null },
  { value: "4:3", label: "4:3 · liggjande", ratio: 4 / 3 },
  { value: "3:2", label: "3:2 · foto", ratio: 3 / 2 },
  { value: "16:9", label: "16:9 · breitt", ratio: 16 / 9 },
  { value: "1:1", label: "1:1 · kvadrat", ratio: 1 },
  { value: "3:4", label: "3:4 · ståande", ratio: 3 / 4 },
  { value: "2:3", label: "2:3 · plakat", ratio: 2 / 3 },
];

export const DEFAULT_DISPLAY = { fit: "auto", aspect: "auto", focus: { x: 50, y: 50 } };
const FRAME = 4 / 3;        // the house's standard frame for photographs
const MIN_RATIO = 2 / 3;    // a whole picture never gets a frame taller than 2:3 …
const MAX_RATIO = 16 / 9;   // … or wider than 16:9 – extreme formats are shown whole inside those bounds
const CROP_TOLERANCE = 0.15;   // 3:2 photographs (12.5 % crop) still fill the frame; 16:9, squares and portraits are shown whole

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const pct = (v, d = 50) => (Number.isFinite(Number(v)) ? clamp(Math.round(Number(v)), 0, 100) : d);

export const normalizeDisplay = (d) => ({
  fit: FITS.some((f) => f.value === d?.fit) ? d.fit : "auto",
  aspect: ASPECTS.some((a) => a.value === d?.aspect) ? d.aspect : "auto",
  focus: { x: pct(d?.focus?.x), y: pct(d?.focus?.y) },
});

export const isDefaultDisplay = (d) => {
  const n = normalizeDisplay(d);
  return n.fit === "auto" && n.aspect === "auto" && n.focus.x === 50 && n.focus.y === 50;
};

export const ratioOf = (aspect) => ASPECTS.find((a) => a.value === aspect)?.ratio ?? null;

/** Settings + the picture's natural size → what the frame actually does. `dims` may be missing (external URL, or a
 * legacy picture before the first measurement) – then the picture fills the standard frame until it has loaded and
 * EventImage re-resolves with the real size. `minRatio` caps how tall a whole-picture frame may get (grids). */
export const resolveDisplay = (settings, dims, { minRatio = MIN_RATIO } = {}) => {
  const d = normalizeDisplay(settings);
  const natural = dims?.w > 0 && dims?.h > 0 ? dims.w / dims.h : null;
  let fit = d.fit;
  if (fit === "auto") fit = natural == null ? "cover" : Math.abs(natural - FRAME) / FRAME > CROP_TOLERANCE ? "contain" : "cover";
  let ratio = ratioOf(d.aspect);
  if (ratio == null) ratio = fit === "contain" ? clamp(natural ?? FRAME, Math.max(MIN_RATIO, minRatio), MAX_RATIO) : FRAME;
  const bands = fit === "contain" && (natural == null || Math.abs(natural - ratio) / ratio > 0.02);
  return { fit, ratio, focus: d.focus, bands, natural, settings: d, minRatio };
};

/** The resolved display for one of an event's pictures, using the dimensions the API sends along (`image_dims`). */
export const displayFor = (ev, src, opts) => resolveDisplay(ev?.display?.[src], ev?.image_dims?.[src], opts);

/** Only what the editor chose outright – gallery tiles honour an explicit «Heile biletet»/focus and otherwise keep their own cover. */
export const explicitFit = (display, src) => {
  const s = display?.[src];
  if (!s || !s.fit || s.fit === "auto") return { fit: "cover", style: undefined };
  const f = s.focus || {};
  const x = Number.isFinite(f.x) ? f.x : 50;
  const y = Number.isFinite(f.y) ? f.y : 50;
  return { fit: s.fit, style: s.fit === "cover" ? { objectPosition: `${x}% ${y}%` } : undefined };
};

export const fitLabel = (fit) => FITS.find((f) => f.value === fit)?.label || fit;

/** Short human description of a picture's settings for badges: «Heile biletet · 2:3», «Fyll ramma · fokus», «Automatisk». */
export const displaySummary = (settings) => {
  const d = normalizeDisplay(settings);
  const bits = [fitLabel(d.fit)];
  if (d.aspect !== "auto") bits.push(d.aspect);
  if (d.fit === "cover" && (d.focus.x !== 50 || d.focus.y !== 50)) bits.push("fokus sett");
  return bits.join(" · ");
};
